import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Package, Users2, Target, FileText, ClipboardList,
  CalendarClock, ShoppingCart, BarChart3, TrendingUp, UserCog, Settings,
  CreditCard, ShieldCheck, LogOut, Search, Bell, Menu, X, Globe2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, ROLE_LABELS, errMsg } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { TrialBanner } from "@/components/common/TrialBanner";
import { TrialExpired } from "@/components/common/TrialExpired";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: "all" },
  { to: "/products", label: "Products", icon: Package, roles: "all" },
  { to: "/buyers", label: "Buyers", icon: Users2, roles: "all" },
  { to: "/leads", label: "Leads", icon: Target, roles: "all" },
  { to: "/rfqs", label: "RFQs", icon: FileText, roles: "all" },
  { to: "/quotations", label: "Quotations", icon: ClipboardList, roles: "all" },
  { to: "/followups", label: "Follow-ups", icon: CalendarClock, roles: "all", badge: true },
  { to: "/orders", label: "Orders", icon: ShoppingCart, roles: "all" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, roles: "all" },
  { to: "/roi", label: "ROI & Break-even", icon: TrendingUp, roles: "all" },
  { to: "/team", label: "Sales Team", icon: Users2, roles: ["company_admin", "sales_manager", "super_admin"] },
  { to: "/users", label: "Users & Team", icon: UserCog, roles: ["company_admin", "super_admin"] },
  { to: "/settings", label: "Company Settings", icon: Settings, roles: ["company_admin", "super_admin"] },
  { to: "/subscription", label: "Subscription", icon: CreditCard, roles: ["company_admin", "super_admin"] },
  { to: "/platform", label: "Platform Admin", icon: ShieldCheck, roles: ["super_admin"] },
];

function visible(item, role) {
  if (item.roles === "all") return true;
  return item.roles.includes(role);
}

function SidebarContent({ role, overdueCount, onNavigate }) {
  return (
    <div className="flex h-full flex-col bg-slate-900 text-slate-100">
      <div className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-800">
        <div className="h-9 w-9 rounded-lg bg-sky-500 flex items-center justify-center flex-shrink-0">
          <Globe2 className="h-5 w-5 text-white" />
        </div>
        <div className="leading-tight">
          <p className="font-bold text-sm font-display">PharmaExport</p>
          <p className="text-[10px] text-slate-400 tracking-wide">GROWTH OS</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
        {NAV.filter((n) => visible(n, role)).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            onClick={onNavigate}
            data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}-link`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                isActive ? "bg-slate-800 text-white font-medium" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
              }`
            }
          >
            <n.icon className="h-[18px] w-[18px] flex-shrink-0" />
            <span className="flex-1">{n.label}</span>
            {n.badge && overdueCount > 0 && (
              <span className="rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{overdueCount}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-3 border-t border-slate-800">
        <p className="text-[10px] text-slate-500 leading-relaxed">
          Buyer interest → Export business → Measurable ROI
        </p>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const { user, company, logout } = useAuth();
  const navigate = useNavigate();
  const trialExpired = company?.status === "trial" && company?.trial_expired;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [overdue, setOverdue] = useState(0);
  const [notes, setNotes] = useState([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    api.get("/followups/summary").then(({ data }) => setOverdue(data.overdue.length)).catch(() => {});
    api.get("/notifications").then(({ data }) => setNotes(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(search)}`).then(({ data }) => setResults(data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const moduleRoute = { buyers: "/buyers", products: "/products", leads: "/leads", rfqs: "/rfqs", quotations: "/quotations", orders: "/orders" };

  if (trialExpired) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900 overflow-y-auto">
        <TrialExpired />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden">
      <aside className="hidden lg:block w-64 flex-shrink-0">
        <SidebarContent role={user.role} overdueCount={overdue} />
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 px-4 sm:px-6 flex items-center justify-between gap-3 flex-shrink-0 z-30">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100" data-testid="mobile-menu-button">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64 border-0">
                <SidebarContent role={user.role} overdueCount={overdue} onNavigate={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>

            <Popover open={results.length > 0}>
              <PopoverTrigger asChild>
                <div className="relative max-w-md w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    data-testid="global-search-input"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search buyers, leads, RFQs, orders…"
                    className="pl-9 h-9 bg-slate-50 border-slate-200"
                  />
                </div>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 p-1 bg-white border border-slate-200 shadow-lg" onOpenAutoFocus={(e) => e.preventDefault()}>
                {results.map((r) => (
                  <button
                    key={`${r.module}-${r.id}`}
                    onClick={() => { navigate(moduleRoute[r.module]); setSearch(""); setResults([]); }}
                    className="w-full text-left px-3 py-2 rounded-md hover:bg-slate-50 flex items-center justify-between"
                    data-testid={`search-result-${r.id}`}
                  >
                    <span className="text-sm text-slate-700 truncate">{r.label}</span>
                    <span className="text-[10px] uppercase text-slate-400 font-semibold">{r.type}</span>
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <button className="relative p-2 rounded-lg hover:bg-slate-100" data-testid="notifications-button">
                  <Bell className="h-5 w-5 text-slate-600" />
                  {notes.length > 0 && <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-rose-500" />}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0 bg-white border border-slate-200 shadow-lg">
                <div className="px-4 py-3 border-b border-slate-100 font-semibold text-sm">Notifications</div>
                <div className="max-h-80 overflow-y-auto">
                  {notes.length === 0 ? (
                    <p className="px-4 py-6 text-center text-sm text-slate-400">You're all caught up.</p>
                  ) : notes.map((n, i) => (
                    <div key={i} className="px-4 py-3 border-b border-slate-50 flex gap-2.5" data-testid={`notification-${i}`}>
                      <span className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${n.type === "overdue" ? "bg-rose-500" : n.type === "due" ? "bg-amber-500" : "bg-sky-500"}`} />
                      <p className="text-sm text-slate-700">{n.message}</p>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-slate-100" data-testid="user-menu-button">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-slate-900 text-white text-xs font-semibold">
                      {(user.name || "U").split(" ").map((x) => x[0]).slice(0, 2).join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:block text-left leading-tight">
                    <p className="text-sm font-medium text-slate-800">{user.name}</p>
                    <p className="text-[11px] text-slate-500">{ROLE_LABELS[user.role]}</p>
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56 p-1 bg-white border border-slate-200 shadow-lg">
                <div className="px-3 py-2 border-b border-slate-100">
                  <p className="text-sm font-medium text-slate-800">{company?.name || "Platform"}</p>
                  <p className="text-xs text-slate-500 truncate">{user.email}</p>
                </div>
                <button onClick={logout} className="mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-rose-600 hover:bg-rose-50" data-testid="logout-button">
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              </PopoverContent>
            </Popover>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto overflow-x-hidden bg-slate-50 p-4 sm:p-6 lg:p-8">
          {trialExpired ? (
            <TrialExpired />
          ) : (
            <>
              <TrialBanner />
              <Outlet />
            </>
          )}
        </main>
      </div>
    </div>
  );
}
